<template>
  <div
    class="leistungsextra-container"
    @click="emitChecked(!checkboxValue)"
    @keydown.prevent.space="emitChecked(!checkboxValue)"
    @keydown.prevent.enter="emitChecked(!checkboxValue)"
    :aria-checked="checkboxValue"
    tabindex="0"
    role="checkbox"
    :aria-label="$t('insuranceCover.extras.' + kaskodeckung.toLowerCase() + '.' + label)"
  >
    <div class="leistungsextra-top centering">
      <img
        alt="Leistungsextra Icon"
        :src="
          baseUrl +
          '/assets/images/icons/' +
          kaskodeckung.toUpperCase() +
          '_' +
          label +
          '.svg'
        "
        class="leistungsextra-icon"
      />
      <button
        class="icon-vorteile icon-info-i info-icon-button"
        :class="{ active: showLexExtrasModal }"
        type="button"
        :aria-expanded="showLexExtrasModal"
        @click.stop="openInfo"
        @keydown.enter.prevent="openInfo"
        @keydown.space.prevent="openInfo"
        role="button"
        tabindex="0"
        :aria-label="
          $t('insuranceCover.extras.info.open', {
            name: $t('insuranceCover.extras.' + kaskodeckung.toLowerCase() + '.' + label)
          })
        "
      ></button>
    </div>
    <div class="leistungsextra-label centering">
      {{ $t('insuranceCover.extras.' + kaskodeckung.toLowerCase() + '.' + label) }}
    </div>

    <div class="leistungsextra-checkbox">
      <div class="click-wrapper" @click.stop="stopTheEvent">
        <Checkbox
          v-model="checkboxValue"
          :styleOption="StyleOption.SECONDARY"
          class="checkbox-component centering2"
          @change="emitChecked"
          :tabindex="-1"
        >
          <template #label>
            <div v-if="checkboxValue" class="checkbox-label">
              {{ $t('insuranceCover.extras.selected') }}
            </div>
            <div v-else class="checkbox-label">
              {{ $t('insuranceCover.extras.select') }}
            </div>
          </template>
        </Checkbox>

        <Modal
          v-if="showLexExtrasModal"
          :title="
            $t(
              'insuranceCover.extras.' +
                props.kaskodeckung.toLowerCase() +
                '.' +
                props.label
            )
          "
          :maxWidth="600"
          :showModal="showLexExtrasModal"
          :showCloseIcon="true"
          :headerDivider="false"
          @closeModal="closeLexExtraModal"
        >
          <template #content>
            {{ $t('insuranceCover.extras.descriptions.' + label.toUpperCase()) }}
          </template>
        </Modal>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Checkbox, Modal, StyleOption } from 'de-zurich-ui-library'
import { onBeforeMount, ref, watch } from 'vue'

const props = defineProps({
  value: {
    type: Boolean,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  kaskodeckung: {
    type: String,
    required: true
  }
})

const emit = defineEmits<{
  input: [value1: boolean, value2: string, value3: string]
}>()

const baseUrl = globalThis.ENV_CONFIG?.ASSETS_BASE_URL.CUSTOMER_PORTAL || ''

const checkboxValue = ref(false)
const showLexExtrasModal = ref(false)

onBeforeMount(() => {
  checkboxValue.value = props.value
})

watch(
  () => props.value,
  (newValue) => {
    checkboxValue.value = newValue
  }
)

function emitChecked(value: boolean): void {
  checkboxValue.value = value
  emit('input', value, props.label, props.kaskodeckung)
}

function stopTheEvent(event: Event): void {
  event.stopImmediatePropagation()
}

function openInfo(): void {
  showLexExtrasModal.value = true
}

function closeLexExtraModal(): void {
  showLexExtrasModal.value = false
}
</script>

<style lang="scss" scoped>
.click-wrapper {
  width: max-content;
  margin: auto;
}

.leistungsextra-container {
  width: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--secondary-color-2-1);
  padding: 16px;
  margin: 12px;
  border-radius: 16px;
  cursor: pointer;
  position: relative;
}

.leistungsextra-container:hover {
  background-color: var(--secondary-color-3);
}

.leistungsextra-checkbox:focus-visible,
button.icon-vorteile.icon-info-i.info-icon-button:focus-visible {
  outline: 2px solid var(--primary-color-0);
  outline-offset: 2px;
}

button.icon-vorteile.icon-info-i.info-icon-button:focus-visible {
  padding-left: 1px;
}

@media (max-width: 799px) {
  .leistungsextra-container {
    width: 100%;
    margin: 0 0 24px 0;
    height: 196px;
  }
}

.centering {
  margin-left: auto;
  margin-right: auto;
}

.centering2 {
  display: flex;
  justify-content: center;
}

.leistungsextra-checkbox {
  background-color: white;
  border-radius: 60px;
  padding: 2px 12px 2px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.leistungsextra-label {
  text-align: center;
}

.leistungsextra-icon {
  height: 59px;
  width: 59px;
  background-color: white;
  border-radius: 50%;
  padding: 5px;
}

button.icon-vorteile.icon-info-i.info-icon-button {
  font-size: 27px;
  color: var(--secondary-color-2);
  background-color: #fff;
  position: absolute;
  top: 8px;
  right: 8px;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  padding: 0;
  line-height: 1;
  z-index: 2;
}

button.icon-vorteile.icon-info-i.info-icon-button:hover,
button.icon-vorteile.icon-info-i.info-icon-button:focus-visible {
  color: var(--primary-color-0);
}

.leistungsextra-container button.info-button {
  position: absolute;
  /* Position near top-right edge of circular icon (slightly overlapping) */
  top: -6px;
  right: -6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: var(--primary-color-2);
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease;
  & span {
    font-size: 12px;
    color: #fff;
    font-family: monospace;
  }
}

.info-button:hover,
.info-button:focus-visible {
  background: var(--primary-color-1);
  box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.05);
}
.info-button:active {
  transform: scale(0.9);
}
.info-button.active {
  background: var(--lex-info-bg-active, var(--accent-color-0, var(--primary-color-2)));
}

:deep(.checkbox) {
  padding: 0 5px;
}

.checkbox-label {
  color: var(--link-color);
}
</style>
